// What the floor LOOKS like, and — because the two should never disagree —
// what it therefore IS acoustically.
//
// The editor already let you paint the floor a colour and, separately, pick
// its acoustic material. Nothing tied them together, so a floor could look
// like oak and be solved as heavy carpet, and nothing on screen would say so.
// A finish here carries both: picking "Herringbone oak" sets the look AND
// `surfaceMaterials.floor = hardwood_concrete`. The material picker stays as
// an explicit override for the case where the two genuinely differ (engineered
// oak floating over concrete vs the same boards on joists sound different and
// look identical).
//
// Finishes are PROCEDURAL — a family of shader plus parameters, not a texture
// download. Three reasons that is the right trade here rather than a
// compromise:
//
//   * A floor is viewed at grazing angles across its whole length. A tiled
//     photo repeats visibly and blurs into the distance; a procedural pattern
//     stays sharp at any distance and never repeats.
//   * The geometric families ARE grids. Plank seams, herringbone, tile grout —
//     a photo stores an approximation of something we can state exactly, and
//     stores it at one fixed resolution.
//   * Variation is free. Six families with parameters give far more distinct
//     finishes than six texture sets, at zero bytes and no licensing.
//
// This module is deliberately pure data: no GLSL, no three, no DOM. The
// shaders that consume it live in the app layer; keeping the vocabulary here
// means the picker, the serializer and (eventually) mobile all agree on what
// finishes exist without any of them importing a renderer.

/**
 * Acoustic floor materials, mirroring `FLOOR_MATERIALS` in
 * backend/materials.py. Pinned by a test that reads that file directly, so a
 * rename on either side fails loudly instead of silently resolving to the
 * catalog default.
 */
export type FloorAcousticId =
  | 'concrete'
  | 'tile'
  | 'vinyl_lino'
  | 'hardwood_joist'
  | 'hardwood_concrete'
  | 'carpet_thin'
  | 'carpet_thick';

/** Which procedural shader draws a finish. */
export type FloorFinishFamily = 'plank' | 'parquet' | 'tile' | 'stone' | 'carpet' | 'concrete';

/** Boards running in one direction — oak, walnut, pine, bamboo. */
export interface PlankParams {
  family: 'plank';
  /** Board width and length, metres. */
  widthM: number;
  lengthM: number;
  /** Direction the boards run, degrees clockwise from the room's +X axis. */
  angleDeg: number;
  /** Visible seam between boards, metres. Zero reads as a continuous sheet. */
  gapM: number;
  /** How far consecutive rows are offset along their length, 0..1. 0.5 is the
   *  common half-bond; 0 stacks the ends into a grid, which reads as tile. */
  stagger: number;
  /** Grain contrast, 0..1. */
  grain: number;
  /** Per-board tone spread, 0..1 — real boards are not all one colour. */
  variation: number;
}

/** Short blocks laid in a repeating figure — herringbone, chevron, basket. */
export interface ParquetParams {
  family: 'parquet';
  blockLengthM: number;
  blockWidthM: number;
  angleDeg: number;
  grain: number;
  variation: number;
  /** Herringbone steps by one block width; chevron mitres the ends instead. */
  pattern: 'herringbone' | 'chevron' | 'basket';
}

/** Regular units with grout between them — ceramic, porcelain, slate. */
export interface TileParams {
  family: 'tile';
  tileM: number;
  /** Grout line width, metres. */
  groutM: number;
  groutColor: string;
  angleDeg: number;
  /** Row-to-row offset, 0..1. 0 is a stack bond, 0.5 a running bond. */
  stagger: number;
  variation: number;
}

/** Veined or mottled stone — marble, terrazzo, granite. */
export interface StoneParams {
  family: 'stone';
  /** Vein contrast and how tightly the veining is folded. */
  veinStrength: number;
  veinScale: number;
  veinColor: string;
  /** Slab size, metres. Zero means one continuous unbroken surface. */
  slabM: number;
  groutM: number;
}

/** Pile — loop, cut, or a woven weave. */
export interface CarpetParams {
  family: 'carpet';
  /** Fibre noise scale (metres) and strength. */
  pileScale: number;
  pileStrength: number;
  /** Weave line spacing, metres. Zero for a plain cut pile. */
  weaveM: number;
}

/** Poured or polished slab — screed, power-float, microcement. */
export interface ConcreteParams {
  family: 'concrete';
  /** Blotchiness scale (metres) and strength. */
  mottleScale: number;
  mottleStrength: number;
  /** Aggregate speckle density, 0..1. */
  aggregate: number;
  /** Control-joint spacing, metres. Zero for an unjointed pour. */
  jointM: number;
}

export type FloorFinishParams =
  | PlankParams | ParquetParams | TileParams
  | StoneParams | CarpetParams | ConcreteParams;

/**
 * Tuned wood species, ported verbatim from three.js's `WoodNodeMaterial`
 * (examples/jsm/materials/WoodNodeMaterial.js, MIT), which ports Blender's
 * procedural wood node.
 *
 * These numbers are the reason the wood in this app is not hand-tuned. Nobody
 * arrives at "walnut" by reasoning about ring bias and splotch intensity —
 * somebody sat and tuned seventeen parameters per species until each one read
 * as itself. Copied rather than re-derived, and kept in the same units and
 * spelling as the source so they can be diffed against it after a three.js
 * upgrade.
 *
 * `ringThickness` is stored as the source's reciprocal form (1/34, not 34):
 * the shader divides by it, and rewriting it "more readably" would silently
 * invert every species.
 */
export interface WoodSpecies {
  centerSize: number;
  largeWarpScale: number;
  largeGrainStretch: number;
  smallWarpStrength: number;
  smallWarpScale: number;
  fineWarpStrength: number;
  fineWarpScale: number;
  ringThickness: number;
  ringBias: number;
  ringSizeVariance: number;
  ringVarianceScale: number;
  barkThickness: number;
  splotchScale: number;
  splotchIntensity: number;
  darkGrainColor: string;
  lightGrainColor: string;
}

export const WOOD_SPECIES: Record<string, WoodSpecies> = {
  teak: {
    centerSize: 1.11, largeWarpScale: 0.32, largeGrainStretch: 0.24, smallWarpStrength: 0.059,
    smallWarpScale: 2, fineWarpStrength: 0.006, fineWarpScale: 32.8, ringThickness: 1 / 34,
    ringBias: 0.03, ringSizeVariance: 0.03, ringVarianceScale: 4.4, barkThickness: 0.3,
    splotchScale: 0.2, splotchIntensity: 0.541,
    darkGrainColor: '#0c0504', lightGrainColor: '#926c50',
  },
  walnut: {
    centerSize: 1.07, largeWarpScale: 0.42, largeGrainStretch: 0.34, smallWarpStrength: 0.016,
    smallWarpScale: 10.3, fineWarpStrength: 0.028, fineWarpScale: 12.7, ringThickness: 1 / 32,
    ringBias: 0.08, ringSizeVariance: 0.03, ringVarianceScale: 5.5, barkThickness: 0.98,
    splotchScale: 1.84, splotchIntensity: 0.97,
    darkGrainColor: '#311e13', lightGrainColor: '#523424',
  },
  white_oak: {
    centerSize: 1.23, largeWarpScale: 0.21, largeGrainStretch: 0.21, smallWarpStrength: 0.034,
    smallWarpScale: 2.44, fineWarpStrength: 0.01, fineWarpScale: 14.3, ringThickness: 1 / 34,
    ringBias: 0.82, ringSizeVariance: 0.16, ringVarianceScale: 1.4, barkThickness: 0.7,
    splotchScale: 0.2, splotchIntensity: 0.541,
    darkGrainColor: '#8b4c21', lightGrainColor: '#c57e43',
  },
  pine: {
    centerSize: 1.23, largeWarpScale: 0.21, largeGrainStretch: 0.18, smallWarpStrength: 0.041,
    smallWarpScale: 2.44, fineWarpStrength: 0.006, fineWarpScale: 23.2, ringThickness: 1 / 24,
    ringBias: 0.1, ringSizeVariance: 0.07, ringVarianceScale: 5, barkThickness: 0.35,
    splotchScale: 0.51, splotchIntensity: 3.32,
    darkGrainColor: '#c58355', lightGrainColor: '#d19d61',
  },
  poplar: {
    centerSize: 1.43, largeWarpScale: 0.33, largeGrainStretch: 0.18, smallWarpStrength: 0.04,
    smallWarpScale: 4.3, fineWarpStrength: 0.004, fineWarpScale: 33.6, ringThickness: 1 / 37,
    ringBias: 0.07, ringSizeVariance: 0.03, ringVarianceScale: 3.8, barkThickness: 0.3,
    splotchScale: 1.92, splotchIntensity: 0.71,
    darkGrainColor: '#716347', lightGrainColor: '#998966',
  },
  maple: {
    centerSize: 1.4, largeWarpScale: 0.38, largeGrainStretch: 0.25, smallWarpStrength: 0.067,
    smallWarpScale: 2.5, fineWarpStrength: 0.005, fineWarpScale: 33.6, ringThickness: 1 / 35,
    ringBias: 0.1, ringSizeVariance: 0.07, ringVarianceScale: 4.6, barkThickness: 0.61,
    splotchScale: 0.46, splotchIntensity: 1.49,
    darkGrainColor: '#b08969', lightGrainColor: '#bc9d7d',
  },
  red_oak: {
    centerSize: 1.21, largeWarpScale: 0.24, largeGrainStretch: 0.25, smallWarpStrength: 0.044,
    smallWarpScale: 2.54, fineWarpStrength: 0.01, fineWarpScale: 14.5, ringThickness: 1 / 34,
    ringBias: 0.92, ringSizeVariance: 0.03, ringVarianceScale: 5.6, barkThickness: 1.01,
    splotchScale: 0.28, splotchIntensity: 3.48,
    darkGrainColor: '#af613b', lightGrainColor: '#e0a27a',
  },
  cherry: {
    centerSize: 1.33, largeWarpScale: 0.11, largeGrainStretch: 0.33, smallWarpStrength: 0.024,
    smallWarpScale: 2.48, fineWarpStrength: 0.01, fineWarpScale: 15.3, ringThickness: 1 / 36,
    ringBias: 0.02, ringSizeVariance: 0.04, ringVarianceScale: 6.5, barkThickness: 0.09,
    splotchScale: 1.27, splotchIntensity: 1.24,
    darkGrainColor: '#913f27', lightGrainColor: '#b45837',
  },
  cedar: {
    centerSize: 1.11, largeWarpScale: 0.39, largeGrainStretch: 0.12, smallWarpStrength: 0.061,
    smallWarpScale: 1.9, fineWarpStrength: 0.006, fineWarpScale: 4.8, ringThickness: 1 / 25,
    ringBias: 0.01, ringSizeVariance: 0.07, ringVarianceScale: 6.7, barkThickness: 0.1,
    splotchScale: 0.61, splotchIntensity: 2.54,
    darkGrainColor: '#9a5b49', lightGrainColor: '#ae745e',
  },
  mahogany: {
    centerSize: 1.25, largeWarpScale: 0.26, largeGrainStretch: 0.29, smallWarpStrength: 0.044,
    smallWarpScale: 2.54, fineWarpStrength: 0.01, fineWarpScale: 15.3, ringThickness: 1 / 38,
    ringBias: 0.01, ringSizeVariance: 0.33, ringVarianceScale: 1.2, barkThickness: 0.07,
    splotchScale: 0.77, splotchIntensity: 1.39,
    darkGrainColor: '#501d12', lightGrainColor: '#6d3722',
  },
};

export type WoodSpeciesId = keyof typeof WOOD_SPECIES;

export interface FloorFinish {
  id: FloorFinishId;
  label: string;
  /** Grouping for the picker. */
  group: 'Wood' | 'Stone & tile' | 'Soft' | 'Poured';
  /** Base colour the family's pattern modulates. */
  base: string;
  /**
   * The acoustic material this finish IS.
   *
   * Picking a look sets the physics, which is the whole point: the two used to
   * be independent controls that could silently disagree. Where one look
   * genuinely spans two behaviours — the same oak boards on joists ring far
   * more than on a slab — they are separate finishes rather than one finish
   * with a hidden choice.
   */
  acoustic: FloorAcousticId;
  /** Which tuned species the wood families draw with. Ignored by the others. */
  woodSpecies?: WoodSpeciesId;
  params: FloorFinishParams;
}

export type FloorFinishId =
  | 'oak_plank' | 'oak_plank_joist' | 'walnut_plank' | 'ash_plank_wide' | 'pine_plank'
  | 'herringbone_oak' | 'chevron_walnut' | 'basket_oak'
  | 'ceramic_tile' | 'slate_tile' | 'terracotta_tile'
  | 'marble_carrara' | 'marble_nero' | 'terrazzo'
  | 'carpet_loop' | 'carpet_cut' | 'carpet_woven'
  | 'concrete_polished' | 'concrete_screed' | 'vinyl_sheet';

/**
 * Every finish, keyed by id.
 *
 * `Record<FloorFinishId, FloorFinish>` on purpose: adding an id to the union
 * without adding its entry is a COMPILE error rather than a runtime hole. This
 * repo has watched an id → thing map drift behind its registry twice in one
 * week, both times caught only after the fact, and the standing rule is that a
 * new registry-driven map ships with the gap closed at the type level.
 */
export const FLOOR_FINISHES: Record<FloorFinishId, FloorFinish> = {
  oak_plank: {
    id: 'oak_plank', label: 'Oak boards', group: 'Wood', base: '#b08a5c',
    acoustic: 'hardwood_concrete',
    woodSpecies: 'white_oak',
    params: {
      family: 'plank', widthM: 0.19, lengthM: 1.8, angleDeg: 0,
      gapM: 0.0015, stagger: 0.5, grain: 0.55, variation: 0.18,
    },
  },
  oak_plank_joist: {
    id: 'oak_plank_joist', label: 'Oak boards, suspended', group: 'Wood', base: '#b08a5c',
    // Same boards, hollow beneath. A suspended timber floor is a membrane: it
    // absorbs several times as much at 125 Hz as the identical boards bonded
    // to a slab, which is exactly the difference a room's bass depends on and
    // exactly what you cannot see. Hence two finishes, not one.
    acoustic: 'hardwood_joist',
    woodSpecies: 'white_oak',
    params: {
      family: 'plank', widthM: 0.19, lengthM: 1.8, angleDeg: 0,
      gapM: 0.002, stagger: 0.5, grain: 0.55, variation: 0.2,
    },
  },
  walnut_plank: {
    id: 'walnut_plank', label: 'Walnut boards', group: 'Wood', base: '#6b4a34',
    acoustic: 'hardwood_concrete',
    woodSpecies: 'walnut',
    params: {
      family: 'plank', widthM: 0.16, lengthM: 1.6, angleDeg: 0,
      gapM: 0.0015, stagger: 0.5, grain: 0.62, variation: 0.16,
    },
  },
  ash_plank_wide: {
    id: 'ash_plank_wide', label: 'Ash, wide board', group: 'Wood', base: '#c8ab84',
    acoustic: 'hardwood_concrete',
    woodSpecies: 'maple',
    params: {
      family: 'plank', widthM: 0.26, lengthM: 2.4, angleDeg: 0,
      gapM: 0.001, stagger: 0.33, grain: 0.4, variation: 0.12,
    },
  },
  pine_plank: {
    id: 'pine_plank', label: 'Pine boards', group: 'Wood', base: '#d2ac74',
    acoustic: 'hardwood_joist',
    woodSpecies: 'pine',
    params: {
      family: 'plank', widthM: 0.14, lengthM: 3, angleDeg: 0,
      gapM: 0.003, stagger: 0.5, grain: 0.7, variation: 0.24,
    },
  },
  herringbone_oak: {
    id: 'herringbone_oak', label: 'Oak herringbone', group: 'Wood', base: '#b08a5c',
    acoustic: 'hardwood_concrete',
    woodSpecies: 'white_oak',
    params: {
      family: 'parquet', blockLengthM: 0.6, blockWidthM: 0.12, angleDeg: 45,
      grain: 0.5, variation: 0.2, pattern: 'herringbone',
    },
  },
  chevron_walnut: {
    id: 'chevron_walnut', label: 'Walnut chevron', group: 'Wood', base: '#6b4a34',
    acoustic: 'hardwood_concrete',
    woodSpecies: 'walnut',
    params: {
      family: 'parquet', blockLengthM: 0.7, blockWidthM: 0.13, angleDeg: 0,
      grain: 0.55, variation: 0.18, pattern: 'chevron',
    },
  },
  basket_oak: {
    id: 'basket_oak', label: 'Oak basketweave', group: 'Wood', base: '#b08a5c',
    acoustic: 'hardwood_concrete',
    woodSpecies: 'red_oak',
    params: {
      family: 'parquet', blockLengthM: 0.36, blockWidthM: 0.12, angleDeg: 0,
      grain: 0.45, variation: 0.2, pattern: 'basket',
    },
  },
  ceramic_tile: {
    id: 'ceramic_tile', label: 'Ceramic tile', group: 'Stone & tile', base: '#d8d4cc',
    acoustic: 'tile',
    params: {
      family: 'tile', tileM: 0.3, groutM: 0.004, groutColor: '#a8a29a',
      angleDeg: 0, stagger: 0, variation: 0.06,
    },
  },
  slate_tile: {
    id: 'slate_tile', label: 'Slate', group: 'Stone & tile', base: '#4a4f55',
    acoustic: 'tile',
    params: {
      family: 'tile', tileM: 0.4, groutM: 0.006, groutColor: '#33373c',
      angleDeg: 0, stagger: 0.5, variation: 0.22,
    },
  },
  terracotta_tile: {
    id: 'terracotta_tile', label: 'Terracotta', group: 'Stone & tile', base: '#b5714b',
    acoustic: 'tile',
    params: {
      family: 'tile', tileM: 0.25, groutM: 0.008, groutColor: '#9c8b76',
      angleDeg: 0, stagger: 0.5, variation: 0.16,
    },
  },
  marble_carrara: {
    id: 'marble_carrara', label: 'Carrara marble', group: 'Stone & tile', base: '#ecebe7',
    acoustic: 'tile',
    params: {
      family: 'stone', veinStrength: 0.5, veinScale: 1.6, veinColor: '#9aa0a6',
      slabM: 0.6, groutM: 0.002,
    },
  },
  marble_nero: {
    id: 'marble_nero', label: 'Nero marble', group: 'Stone & tile', base: '#2b2b2e',
    acoustic: 'tile',
    params: {
      family: 'stone', veinStrength: 0.7, veinScale: 1.2, veinColor: '#d8d4c8',
      slabM: 0.6, groutM: 0.002,
    },
  },
  terrazzo: {
    id: 'terrazzo', label: 'Terrazzo', group: 'Stone & tile', base: '#dedad2',
    acoustic: 'concrete',
    params: {
      family: 'stone', veinStrength: 0.25, veinScale: 6, veinColor: '#8d8477',
      slabM: 0, groutM: 0,
    },
  },
  carpet_loop: {
    id: 'carpet_loop', label: 'Loop pile carpet', group: 'Soft', base: '#8b8478',
    acoustic: 'carpet_thin',
    params: { family: 'carpet', pileScale: 0.012, pileStrength: 0.35, weaveM: 0.01 },
  },
  carpet_cut: {
    id: 'carpet_cut', label: 'Cut pile carpet', group: 'Soft', base: '#7d7466',
    // Deep pile over underlay — the strongly absorbent one.
    acoustic: 'carpet_thick',
    params: { family: 'carpet', pileScale: 0.008, pileStrength: 0.5, weaveM: 0 },
  },
  carpet_woven: {
    id: 'carpet_woven', label: 'Woven carpet', group: 'Soft', base: '#6f6a60',
    acoustic: 'carpet_thin',
    params: { family: 'carpet', pileScale: 0.02, pileStrength: 0.28, weaveM: 0.006 },
  },
  concrete_polished: {
    id: 'concrete_polished', label: 'Polished concrete', group: 'Poured', base: '#b6b4b0',
    acoustic: 'concrete',
    params: {
      family: 'concrete', mottleScale: 1.4, mottleStrength: 0.22,
      aggregate: 0.35, jointM: 1.5,
    },
  },
  concrete_screed: {
    id: 'concrete_screed', label: 'Screed', group: 'Poured', base: '#a5a29c',
    acoustic: 'concrete',
    params: {
      family: 'concrete', mottleScale: 0.8, mottleStrength: 0.35,
      aggregate: 0.12, jointM: 0,
    },
  },
  vinyl_sheet: {
    id: 'vinyl_sheet', label: 'Vinyl sheet', group: 'Poured', base: '#c3bdb2',
    acoustic: 'vinyl_lino',
    params: {
      family: 'concrete', mottleScale: 0.5, mottleStrength: 0.1,
      aggregate: 0.05, jointM: 0,
    },
  },
};

/** Every finish, in picker order. */
export const FLOOR_FINISH_LIST: FloorFinish[] = Object.values(FLOOR_FINISHES);

export function floorFinish(id: string): FloorFinish | null {
  // `hasOwn`, not a plain index: a saved document names a finish as a bare
  // string, so `'constructor'` or `'toString'` would otherwise walk the
  // prototype chain and hand the caller a function where it expected a
  // finish. An unknown id has to be a clean null the renderer can fall back
  // from, whatever the string happens to be.
  return Object.hasOwn(FLOOR_FINISHES, id)
    ? (FLOOR_FINISHES as Record<string, FloorFinish>)[id]
    : null;
}

/** The acoustic material a finish implies — what picking a look should set. */
export function floorFinishAcoustic(id: string): FloorAcousticId | null {
  return floorFinish(id)?.acoustic ?? null;
}
